<div class="view-container">
<mat-card class="order-review-card">
  <div class="order-review-card__header">
    <div class="order-review-card__title-row">
      <div class="order-review-card__icon">
        <mat-icon>assignment</mat-icon>
      </div>
      <div>
        @if (order) {
          <h1 class="order-review-card__title">Order #{{ order.id }}</h1>
          <div class="order-review-card__meta">
            <span [class]="getStatusPillClass(order.status)">
              {{ order.status.status }}
            </span>
            @if (company && !individual) {
              <span class="order-review-card__company">{{ company.name }}</span>
            }
            @if (individual && !company) {
              <span class="order-review-card__company">
                {{ individual.first_name }} {{ individual.last_name }}
              </span>
            }
            @if (getEntityPhone()) {
              <span class="order-review-card__company">{{ getEntityPhone() }}</span>
            }
            @if (order && sageUiEnabled) {
              <span class="order-review-card__sage">
                Sage: {{ getSageSyncLabel() }}
                @if (order.sage_invoice_id) {
                  <span> ({{ order.sage_invoice_id }})</span>
                }
              </span>
            }
          </div>
        }
      </div>
    </div>

    <mat-form-field
      id="order-status"
      appearance="outline"
      class="order-review-card__status"
    >
      <mat-label>Status</mat-label>
      <mat-select
        required
        [disabled]="!perm.isPrescriptionReviewStaff()"
        (selectionChange)="onOrderStatusChanged($event)"
        [formControl]="orderStatusControl"
      >
        @for (status of orderStatuses; track status) {
          <mat-option [value]="status">
            {{ status.status }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
  </div>

  <div class="order-review-card__actions">
    <button mat-stroked-button class="order-review-card__action-btn" (click)="backToList()">
      <mat-icon>arrow_back</mat-icon> Back to list
    </button>
    <button
      [disabled]="!hasOrderItems"
      class="order-review-card__action-btn"
      (click)="openOrderSummary()"
      mat-stroked-button
    >
      <mat-icon>open_in_new</mat-icon> Order summary
    </button>
    @if (checkingPrescriptionFile) {
      <button mat-stroked-button class="order-review-card__action-btn" disabled>
        <mat-icon>hourglass_empty</mat-icon> Checking…
      </button>
    } @else if (prescriptionFileUploaded) {
      <button
        (click)="openPrescriptionFile(order)"
        mat-stroked-button
        class="order-review-card__action-btn"
      >
        <mat-icon>open_in_new</mat-icon> Prescription file
      </button>
    } @else if (order) {
      <button
        (click)="openPrescriptionUploadDialog()"
        mat-stroked-button
        class="order-review-card__action-btn"
        color="primary"
      >
        <mat-icon>cloud_upload</mat-icon> Upload prescription
      </button>
    }
    @if (sageUiEnabled) {
      <button
        *authGroup="['SuperAdmin']"
        (click)="syncOrderToSage()"
        mat-stroked-button
        class="order-review-card__action-btn"
      >
        <mat-icon>sync_alt</mat-icon> Sync to Sage
      </button>
    }
    @if (perm.isPrescriptionReviewStaff()) {
      <button
        class="order-review-card__action-btn"
        [disabled]="orderItems.length <= 1"
        mat-stroked-button
        [routerLink]="orderSplitLink"
      >
        <mat-icon>splitscreen</mat-icon> Split order
      </button>
    }
    <ng-container *authGroup="['SuperAdmin', 'OperationsManager', 'Packaging', 'Fulfilment']">
      @if (order) {
        <button
          [disabled]="!isFulfillable"
          [routerLink]="['/portal/fulfill', order.id]"
          class="order-review-card__action-btn"
          mat-stroked-button
        >
          <mat-icon>done</mat-icon> Fulfill
        </button>
      }
    </ng-container>
    @if (canCaptureWaybill && order) {
      <button
        (click)="waybillOrder(order)"
        class="order-review-card__action-btn"
        mat-stroked-button
      >
        Create waybill
      </button>
    }
    @if (trackable) {
      <button
        (click)="openOrderTrackDialog(order)"
        class="order-review-card__action-btn order-review-card__track"
        mat-stroked-button
      >
        Track
      </button>
    }
    <button mat-stroked-button class="order-review-card__action-btn" (click)="refresh()">
      <mat-icon>refresh</mat-icon>
    </button>
    <button mat-stroked-button class="order-review-card__action-btn" (click)="printOrderToPDF()">
      <mat-icon>print</mat-icon> Print order
    </button>
    @if (showDeliveryNoteButton) {
      <button
        mat-stroked-button
        class="order-review-card__action-btn"
        (click)="printDeliveryNoteToPDF()"
      >
        <mat-icon>print</mat-icon> Delivery note
      </button>
    }
  </div>

  @if (order !== null && !hasOrderItems) {
    <div class="order-review-card__alert">
      <mat-icon>info</mat-icon>
      <span>No medicine has been ordered for this prescription</span>
    </div>
  }

  <div class="order-review-card__tabs">
    <mat-tab-group>
      <mat-tab label="Order items">
        <div class="order-review-card__tab-panel">
        @if (perm.canMutate()) {
          <core-add-medicine
            [orderItemStatuses]="orderItemStatuses"
            (onMedicineAdd)="addOrderItem($event)"
          ></core-add-medicine>
        }
        <div class="order-items-container">
          @if (order) {
            <core-order-item-list
              [order]="order"
              [orderStatus]="selectedStatus"
              [readOnly]="perm.isViewer()"
              (onDelete)="removeOrderItem($event)"
              [orderItems]="orderItems"
              [orderItemStatuses]="orderItemStatuses"
              [batchStatuses]="batchStatuses"
              >
            </core-order-item-list>
            @if (orderItems.length) {
              <dl class="order-review-totals">
                @if (showReviewDeliveryFee && canWaiveDeliveryFee) {
                  <div class="order-review-totals__row order-review-totals__row--action">
                    <button
                      type="button"
                      mat-stroked-button
                      color="warn"
                      data-testid="waive-delivery-fee"
                      [disabled]="waivingDeliveryFee"
                      (click)="waiveDeliveryFee()"
                    >
                      Remove delivery fee
                    </button>
                  </div>
                }
                <div class="order-review-totals__row">
                  <dt>Subtotal</dt>
                  <dd>R {{ reviewMedicineSubtotal.toFixed(2) }}</dd>
                </div>
                @if (reviewPatientDiscount > 0) {
                  <div class="order-review-totals__row">
                    <dt>Patient discount</dt>
                    <dd>-R {{ reviewPatientDiscount.toFixed(2) }}</dd>
                  </div>
                }
                @if (showReviewDeliveryFee) {
                  <div class="order-review-totals__row">
                    <dt>{{ reviewDeliveryFeeLabel }}</dt>
                    <dd>R {{ reviewDeliveryFee.toFixed(2) }}</dd>
                  </div>
                }
                <div class="order-review-totals__row">
                  <dt>VAT (15%)</dt>
                  <dd>R {{ reviewVatAmount.toFixed(2) }}</dd>
                </div>
                <div class="order-review-totals__row order-review-totals__row--total">
                  <dt>Total</dt>
                  <dd>R {{ reviewTotalInclVat.toFixed(2) }}</dd>
                </div>
              </dl>
            }
          }
        </div>
        </div>
      </mat-tab>
      <mat-tab label="Details">
        <div class="order-review-card__tab-panel">
        @if (order) {
          <div class="order-review-card__details-table-wrap details my-2">
            <table>
              <tr>
                <th>Created</th>
                <td>
                  {{ utilsService.formatTime(order.created_at) }}
                </td>
              </tr>
              <tr>
                <th>Updated</th>
                <td>
                  {{ utilsService.formatTime(order.updated_at) }}
                </td>
              </tr>
              <tr>
                <th>Waybill number</th>
                <td>
                  {{ waybillNumber ? waybillNumber : "-" }}
                </td>
              </tr>
              <tr>
                <th>Customer order number</th>
                <td>{{ customerOrderNumber ? customerOrderNumber : "-" }}</td>
              </tr>
            </table>
          </div>
        }
        <mat-accordion multi="true" class="order-review-card__accordion">
          @if (company) {
            <mat-expansion-panel class="order-review-card__panel">
              <mat-expansion-panel-header>
                <mat-panel-title> Company details </mat-panel-title>
                <mat-panel-description> &nbsp; </mat-panel-description>
              </mat-expansion-panel-header>
              <!---------------------------------------Display company details-------------------------------------->
              <div class="details">
                <div class="my-2">
                  @if (perm.canMutate()) {
                    <button
                      (click)="editingCompany = !editingCompany"
                      mat-flat-button
                      color="primary"
                      class="me-2"
                      >
                      {{ getEditCompanyButtonText() }}
                    </button>
                  }
                  @if (!editingCompany && company) {
                    <table class="mt-4">
                      <tr>
                        <th>Name</th>
                        <td>
                          {{ company.name ? company.name : "-" }}
                        </td>
                      </tr>
                      <tr>
                        <th>Alternative email address</th>
                        <td>
                          {{ company.email_alt ? company.email_alt : "-" }}
                        </td>
                      </tr>
                      <tr>
                        <th>Phone number</th>
                        <td>
                          {{ company.phone_number ? company.phone_number : "-" }}
                        </td>
                      </tr>
                      <tr>
                        <th>Email</th>
                        <td>
                          {{ company.email ? company.email : "-" }}
                        </td>
                      </tr>
                      <tr>
                        <th>Alternative phone number</th>
                        <td>
                          {{
                          company.phone_number_alt
                          ? company.phone_number_alt
                          : "-"
                          }}
                        </td>
                      </tr>
                      <tr>
                        <th>Practice number</th>
                        <td>
                          {{
                          company.practice_number ? company.practice_number : "-"
                          }}
                        </td>
                      </tr>
                      <tr>
                        <th>VAT registration number</th>
                        <td>
                          {{
                          company.vat_registration_number
                          ? company.vat_registration_number
                          : "-"
                          }}
                        </td>
                      </tr>
                    </table>
                  }
                </div>
                <!------------------------------------------Edit company details form------------------------------------------->
                @if (editingCompany && company) {
                  <form
                    class="mt-4"
                    [formGroup]="companyDetailsForm"
                    >
                    <h3 class="my-2" mat-card-subtitle>Edit company details</h3>
                    <div class="row">
                      <div class="col-md-6">
                        <p>
                          <mat-form-field appearance="outline">
                            <mat-label>Company name</mat-label>
                            <input
                              required
                              readonly
                              (click)="openSearchCompanyDialog()"
                              data-testid="company-name"
                              [disabled]="isLoading"
                              type="text"
                              placeholder="Company name"
                              matInput
                              formControlName="companyName"
                              />
                            @if (!selectedCompany) {
                              <button
                                type="button"
                                (click)="openSearchCompanyDialog()"
                                mat-icon-button
                                matSuffix
                                [attr.aria-label]="'Search for a company'"
                                >
                                <mat-icon>search</mat-icon>
                              </button>
                            }
                            @if (selectedCompany) {
                              <button
                                type="button"
                                (click)="deselectCompany()"
                                mat-icon-button
                                matSuffix
                                [attr.aria-label]="'Remove customer'"
                                >
                                <mat-icon>cancel</mat-icon>
                              </button>
                            }
                          </mat-form-field>
                        </p>
                        <p>
                          <mat-form-field appearance="outline">
                            <mat-label>Phone number</mat-label>
                            <input
                              required
                              data-testid="company-phone-number"
                              formControlName="phoneNumber"
                              matInput
                              placeholder="Phone number"
                              />
                            @if (companyDetailsForm.get('phoneNumber')?.invalid) {
                              <mat-error
                                >
                                Phone number is invalid
                              </mat-error>
                            }
                          </mat-form-field>
                        </p>
                        <p>
                          <mat-form-field appearance="outline">
                            <mat-label>Alternative phone number</mat-label>
                            <input
                              data-testid="company-phone-number-alt"
                              formControlName="phoneNumberAlt"
                              matInput
                              placeholder="Alternative phone number"
                              />
                            @if (
                              companyDetailsForm.get('phoneNumberAlt')?.invalid
                              ) {
                              <mat-error
                                >
                                Alternative phone number is not valid
                              </mat-error>
                            }
                          </mat-form-field>
                        </p>
                        <p>
                          <mat-form-field appearance="outline">
                            <mat-label>Email address</mat-label>
                            <input
                              required
                              data-testid="company-email"
                              formControlName="email"
                              matInput
                              placeholder="Email address"
                              />
                            @if (companyDetailsForm.get('email')?.invalid) {
                              <mat-error
                                >
                                Email address is invalid
                              </mat-error>
                            }
                          </mat-form-field>
                        </p>
                      </div>
                      <div class="col-md-6">
                        <p>
                          <mat-form-field appearance="outline">
                            <mat-label>Alternative email address</mat-label>
                            <input
                              data-testid="company-email-alt"
                              formControlName="emailAlt"
                              matInput
                              placeholder="Alternative email address"
                              />
                            @if (companyDetailsForm.get('emailAlt')?.invalid) {
                              <mat-error
                                >
                                Alternative email address is not valid
                              </mat-error>
                            }
                          </mat-form-field>
                        </p>
                        <p>
                          <mat-form-field appearance="outline">
                            <mat-label>Practice number</mat-label>
                            <input
                              data-testid="company-practice-number"
                              formControlName="practiceNumber"
                              matInput
                              placeholder="Practice number"
                              />
                            @if (
                              companyDetailsForm.get('practiceNumber')?.invalid
                              ) {
                              <mat-error
                                >
                                Practice number is invalid
                              </mat-error>
                            }
                          </mat-form-field>
                        </p>
                        <p>
                          <mat-form-field appearance="outline">
                            <mat-label>VAT registration number</mat-label>
                            <input
                              data-testid="company-vat-reg-number"
                              formControlName="vatRegistrationNumber"
                              matInput
                              placeholder="VAT registration number"
                              />
                            @if (
                              companyDetailsForm.get('vatRegistrationNumber')
                              ?.invalid
                              ) {
                              <mat-error
                                >
                                VAT registration number is invalid
                              </mat-error>
                            }
                          </mat-form-field>
                        </p>
                        <p>
                          <mat-form-field appearance="outline">
                            <mat-label>Type</mat-label>
                            <mat-select
                              required
                              data-testid="company-type"
                              formControlName="typeOfBusiness"
                              >
                              <mat-option [value]="undefined"
                                >Not Applicable</mat-option
                                >
                                @for (type of companyTypes; track type) {
                                  <mat-option
                                    [value]="type.id"
                                    >
                                    {{ type.type }}
                                  </mat-option>
                                }
                              </mat-select>
                              @if (
                                companyDetailsForm.get('typeOfBusiness')?.invalid
                                ) {
                                <mat-error
                                  >
                                  Select company type
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                        </div>
                      </div>
                      <div class="py-2">
                        <mat-divider></mat-divider>
                      </div>
                    </form>
                  }
                  <!---------------------------------------------- Select and display ompany distributor--------------------------------------->
                  @if (showDistributor) {
                    <div class="my-2">
                      <div class="py-2">
                        <mat-divider></mat-divider>
                      </div>
                      <h3 mat-card-subtitle>Distributor</h3>
                      <p>
                        @if (!selectedDistributor) {
                          <button
                            mat-stroked-button
                            (click)="openSearchDistributorDialog()"
                            >
                            Select distributor
                          </button>
                        }
                      </p>
                      @if (selectedDistributor) {
                        <div class="details mt-2">
                          <div>
                            <!-- Distributor remove: hidden for now; uncomment to restore deselectDistributor(). -->
                            <!--
                            <button
                              mat-stroked-button
                              (click)="deselectDistributor()"
                              class="me-2 my-2"
                              >
                              <mat-icon>cancel</mat-icon> Remove
                            </button>
                            -->
                            <table class="mt-4">
                              <tr>
                                <th>Name</th>
                                <td>
                                  {{
                                  selectedDistributor.name
                                  ? selectedDistributor.name
                                  : "-"
                                  }}
                                </td>
                              </tr>
                              <tr>
                                <th>Alternative email address</th>
                                <td>
                                  {{
                                  selectedDistributor.email_alt
                                  ? selectedDistributor.email_alt
                                  : "-"
                                  }}
                                </td>
                              </tr>
                              <tr>
                                <th>Phone number</th>
                                <td>
                                  {{
                                  selectedDistributor.phone_number
                                  ? selectedDistributor.phone_number
                                  : "-"
                                  }}
                                </td>
                              </tr>
                              <tr>
                                <th>Email</th>
                                <td>
                                  {{
                                  selectedDistributor.email
                                  ? selectedDistributor.email
                                  : "-"
                                  }}
                                </td>
                              </tr>
                              <tr>
                                <th>Alternative phone number</th>
                                <td>
                                  {{
                                  selectedDistributor.phone_number_alt
                                  ? selectedDistributor.phone_number_alt
                                  : "-"
                                  }}
                                </td>
                              </tr>
                              <tr>
                                <th>Practice number</th>
                                <td>
                                  {{
                                  selectedDistributor.practice_number
                                  ? selectedDistributor.practice_number
                                  : "-"
                                  }}
                                </td>
                              </tr>
                              <tr>
                                <th>VAT registration number</th>
                                <td>
                                  {{
                                  selectedDistributor.vat_registration_number
                                  ? selectedDistributor.vat_registration_number
                                  : "-"
                                  }}
                                </td>
                              </tr>
                            </table>
                          </div>
                        </div>
                      }
                    </div>
                  }
                </div>
              </mat-expansion-panel>
            }

            @if (individual) {
              <mat-expansion-panel class="order-review-card__panel">
                <mat-expansion-panel-header>
                  <mat-panel-title> Individual details </mat-panel-title>
                  <mat-panel-description> &nbsp; </mat-panel-description>
                </mat-expansion-panel-header>
                <div class="details">
                  <div class="my-2">
                    @if (perm.canMutate()) {
                      <button
                        (click)="editingIndividual = !editingIndividual"
                        mat-flat-button
                        color="primary"
                        class="me-2"
                        >
                        {{ getEditIndividualButtonText() }}
                      </button>
                    }
                  </div>
                  <!---------------------------------------Display individual details---------------------------------->
                  @if (individual && !editingIndividual) {
                    <div class="mt-4">
                      <table>
                        <tr>
                          <th>Name</th>
                          <td>
                            {{ individual.first_name + " " + individual.last_name }}
                          </td>
                        </tr>
                        <tr>
                          <th>Address 1</th>
                          <td>
                            {{ address ? address.address_1 : "-" }}
                          </td>
                        </tr>
                        <tr>
                          <th>Building number</th>
                          <td>{{ address ? address.building_apt_number : "-" }}</td>
                        </tr>
                        <tr>
                          <th>Province</th>
                          <td>
                            {{ address ? address.province : "-" }}
                          </td>
                        </tr>
                        <tr>
                          <th>City</th>
                          <td>{{ address ? address.city : "-" }}</td>
                        </tr>
                        <tr>
                          <th>Postcode</th>
                          <td>
                            {{ address ? address.postcode : "" }}
                          </td>
                        </tr>
                        <tr>
                          <th>Country</th>
                          <td>
                            {{ address ? address.country : "-" }}
                          </td>
                        </tr>
                        <tr>
                          <th>Phone number</th>
                          <td>
                            {{
                            individual.phone_number ? individual.phone_number : "-"
                            }}
                          </td>
                        </tr>
                        <tr>
                          <th>Alternative phone number</th>
                          <td>
                            {{
                            individual.phone_number_alt
                            ? individual.phone_number_alt
                            : "-"
                            }}
                          </td>
                        </tr>
                        <tr>
                          <th>Email address</th>
                          <td>
                            {{ individual.email ? individual.email : "-" }}
                          </td>
                        </tr>
                        <tr>
                          <th>Alternative email address</th>
                          <td>
                            {{ individual.email_alt ? individual.email_alt : "-" }}
                          </td>
                        </tr>
                      </table>
                    </div>
                  }
                  <!-----------------------------------Edit individual details form ---------------------------------->
                  @if (editingIndividual) {
                    <div>
                      <form class="mt-4" [formGroup]="individualDetailsForm">
                        <h3 class="my-2" mat-card-subtitle>
                          Edit individual details
                        </h3>
                        <div class="row">
                          <div class="col-md-6">
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Title</mat-label>
                                <mat-select required formControlName="title">
                                  <mat-option value="Mr">Mr</mat-option>
                                  <mat-option value="Mrs">Mrs</mat-option>
                                  <mat-option value="Miss">Miss</mat-option>
                                  <mat-option value="Prof">Prof</mat-option>
                                  <mat-option value="Dr">Dr</mat-option>
                                </mat-select>
                                @if (individualDetailsForm.get('title')?.invalid) {
                                  <mat-error
                                    >
                                    Title is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Initials</mat-label>
                                <input
                                  required
                                  formControlName="initials"
                                  matInput
                                  placeholder="Initials"
                                  />
                                @if (
                                  individualDetailsForm.get('initials')?.invalid
                                  ) {
                                  <mat-error
                                    >
                                    Initials are invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>First Name</mat-label>
                                <input
                                  required
                                  formControlName="firstName"
                                  matInput
                                  placeholder="First name"
                                  />
                                @if (
                                  individualDetailsForm.get('firstName')?.invalid
                                  ) {
                                  <mat-error
                                    >
                                    First name is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Surname</mat-label>
                                <input
                                  required
                                  formControlName="lastName"
                                  matInput
                                  placeholder="Surname"
                                  />
                                @if (
                                  individualDetailsForm.get('lastName')?.invalid
                                  ) {
                                  <mat-error
                                    >
                                    Surname is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Gender</mat-label>
                                <mat-select required formControlName="gender">
                                  <mat-option value="male">Male</mat-option>
                                  <mat-option value="female">Female</mat-option>
                                  <mat-option value="other">Other</mat-option>
                                </mat-select>
                                @if (individualDetailsForm.get('gender')?.invalid) {
                                  <mat-error
                                    >
                                    Gender is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Is the individual an animal?</mat-label>
                                <mat-select
                                  required
                                  data-testid="individual-is-animal"
                                  formControlName="isAnimal"
                                  >
                                  <mat-option [value]="false">No</mat-option>
                                  <mat-option [value]="true">Yes</mat-option>
                                </mat-select>
                                @if (
                                  individualDetailsForm.get('isAnimal')?.invalid
                                  ) {
                                  <mat-error
                                    >
                                    Invalid selection
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Date of birth</mat-label>
                                <input
                                  required
                                  formControlName="dateOfBirth"
                                  matInput
                                  [matDatepicker]="picker1"
                                  />
                                <mat-datepicker-toggle matSuffix [for]="picker1">
                                </mat-datepicker-toggle>
                                <mat-datepicker #picker1></mat-datepicker>
                                @if (individualDetailsForm.get('date')?.invalid) {
                                  <mat-error
                                    >
                                    Date of birth is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                          </div>
                          <div class="col-md-6">
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Individual type</mat-label>
                                <mat-select
                                  [disabled]="isLoading"
                                  #chosenValue
                                  formControlName="individualType"
                                  >
                                  @for (type of individualTypes; track type) {
                                    <mat-option
                                      [value]="type.id"
                                      >
                                      {{ type.type }}
                                    </mat-option>
                                  }
                                </mat-select>
                                @if (
                                  individualDetailsForm.get('individualType')
                                  ?.invalid
                                  ) {
                                  <mat-error
                                    >
                                    Individual type is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Phone Number</mat-label>
                                <input
                                  required
                                  formControlName="phoneNumber"
                                  matInput
                                  placeholder="Phone Number"
                                  />
                                @if (
                                  individualDetailsForm.get('phoneNumber')?.invalid
                                  ) {
                                  <mat-error
                                    >
                                    Phone number is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label> Alternative phone number</mat-label>
                                <input
                                  required
                                  formControlName="phoneNumberAlt"
                                  matInput
                                  placeholder="Phone number"
                                  />
                                @if (
                                  individualDetailsForm.get('phoneNumberAlt')
                                  ?.invalid
                                  ) {
                                  <mat-error
                                    >
                                    Phone number is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Email address</mat-label>
                                <input
                                  required
                                  data-testid="individual-email"
                                  formControlName="email"
                                  matInput
                                  placeholder="Email address"
                                  />
                                @if (individualDetailsForm.get('email')?.invalid) {
                                  <mat-error
                                    >
                                    Email address is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Alternative email address</mat-label>
                                <input
                                  data-testid="individual-alt-email"
                                  formControlName="emailAlt"
                                  matInput
                                  placeholder="Email address"
                                  />
                                @if (
                                  individualDetailsForm.get('emailAlt')?.invalid
                                  ) {
                                  <mat-error
                                    >
                                    Alternative email address is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            <p>
                              <mat-form-field appearance="outline">
                                <mat-label>Number Of Dependants</mat-label>
                                <input
                                  type="number"
                                  min="0"
                                  formControlName="numberOfDependants"
                                  matInput
                                  placeholder="Number of dependants"
                                  />
                                @if (
                                  individualDetailsForm.get('numberOfDependants')
                                  ?.invalid
                                  ) {
                                  <mat-error
                                    >
                                    Number of dependants is invalid
                                  </mat-error>
                                }
                              </mat-form-field>
                            </p>
                            `
                          </div>
                        </div>
                      </form>
                    </div>
                  }
                </div>
              </mat-expansion-panel>
            }

            <!-- Viewer: hide section entirely when there is no prescriber (no empty add form). -->
            @if (prescriber || perm.canMutate()) {
              <mat-expansion-panel class="order-review-card__panel">
                <mat-expansion-panel-header>
                  <mat-panel-title> Prescriber details </mat-panel-title>
                  <mat-panel-description> &nbsp; </mat-panel-description>
                </mat-expansion-panel-header>
                <!----------------------------------Display prescriber details--------------------------------->
                <div class="details">
                  <div class="my-2">
                    @if (perm.canMutate()) {
                      <button
                        (click)="editingPrescriber = !editingPrescriber"
                        mat-flat-button
                        color="primary"
                        class="me-2"
                        >
                        {{ getEditPrescriberButtonText() }}
                      </button>
                    }
                    @if (prescriber && !editingPrescriber) {
                      <div class="mt-4">
                        <table>
                          <tr>
                            <th>Name</th>
                            <td>
                              {{ prescriber.first_name + " " + prescriber.last_name }}
                            </td>
                          </tr>
                          <tr>
                            <th>Phone number</th>
                            <td>
                              {{
                              prescriber.phone_number
                              ? prescriber.phone_number
                              : "-"
                              }}
                            </td>
                          </tr>
                          <tr>
                            <th>Alternative phone number</th>
                            <td>
                              {{
                              prescriber.phone_number_alt
                              ? prescriber.phone_number_alt
                              : "-"
                              }}
                            </td>
                          </tr>
                          <tr>
                            <th>Email address</th>
                            <td>
                              {{ prescriber.email ? prescriber.email : "-" }}
                            </td>
                          </tr>
                          <tr>
                            <th>Alternative email address</th>
                            <td>
                              {{ prescriber.email_alt ? prescriber.email_alt : "-" }}
                            </td>
                          </tr>
                        </table>
                      </div>
                    }
                  </div>
                  <!----------------------------------Edit prescriber details----------------------------------------->
                  @if (perm.canMutate() && (editingPrescriber || !prescriber)) {
                    <form
                      class="mt-4"
                      [formGroup]="prescriberDetailsForm"
                      >
                      <h3 class="my-2" mat-card-subtitle>Edit prescriber details</h3>
                      <div class="row">
                        <div class="col-md-6">
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>First name</mat-label>
                              <input
                                required
                                readonly
                                (click)="openSearchPrescriberDialog()"
                                data-testid="individual-first-name"
                                formControlName="firstName"
                                matInput
                                placeholder="Start typing a name..."
                                />
                              @if (
                                prescriberDetailsForm.get('firstName')?.invalid
                                ) {
                                <mat-error
                                  >
                                  First name is invalid
                                </mat-error>
                              }
                              @if (!selectedPrescriber) {
                                <button
                                  type="button"
                                  (click)="openSearchPrescriberDialog()"
                                  mat-icon-button
                                  matSuffix
                                  [attr.aria-label]="'Search for a prescriber'"
                                  >
                                  <mat-icon>search</mat-icon>
                                </button>
                              }
                              @if (selectedPrescriber) {
                                <button
                                  type="button"
                                  (click)="deselectPrescriber()"
                                  mat-icon-button
                                  matSuffix
                                  [attr.aria-label]="'Remove prescriber'"
                                  >
                                  <mat-icon>cancel</mat-icon>
                                </button>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Last name</mat-label>
                              <input
                                required
                                data-testid="individual-last-name"
                                formControlName="lastName"
                                matInput
                                placeholder="Surname"
                                />
                              @if (prescriberDetailsForm.get('lastName')?.invalid) {
                                <mat-error
                                  >
                                  Last name is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Title</mat-label>
                              <mat-select
                                required
                                data-testid="individual-title"
                                formControlName="title"
                                >
                                <mat-option value="Mr">Mr</mat-option>
                                <mat-option value="Mrs">Mrs</mat-option>
                                <mat-option value="Miss">Miss</mat-option>
                                <mat-option value="Prof">Prof</mat-option>
                                <mat-option value="Dr">Dr</mat-option>
                              </mat-select>
                              @if (prescriberDetailsForm.get('title')?.invalid) {
                                <mat-error
                                  >
                                  Title is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Initials</mat-label>
                              <input
                                required
                                data-testid="individual-initials"
                                formControlName="initials"
                                matInput
                                placeholder="Initials"
                                />
                              @if (prescriberDetailsForm.get('initials')?.invalid) {
                                <mat-error
                                  >
                                  Initials are invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Gender</mat-label>
                              <mat-select
                                required
                                data-testid="individual-gender"
                                formControlName="gender"
                                >
                                <mat-option value="male">Male</mat-option>
                                <mat-option value="female">Female</mat-option>
                                <mat-option value="other">Other</mat-option>
                              </mat-select>
                              @if (prescriberDetailsForm.get('gender')?.invalid) {
                                <mat-error
                                  >
                                  Gender is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Date of birth</mat-label>
                              <input
                                required
                                data-testid="individual-date-of-birth"
                                formControlName="dateOfBirth"
                                matInput
                                [matDatepicker]="picker3"
                                />
                              <mat-datepicker-toggle
                                matSuffix
                                [for]="picker3"
                              ></mat-datepicker-toggle>
                              <mat-datepicker #picker3></mat-datepicker>
                              @if (prescriberDetailsForm.get('date')?.invalid) {
                                <mat-error
                                  >
                                  Date of birth is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                        </div>
                        <div class="col-md-6">
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Email address</mat-label>
                              <input
                                required
                                data-testid="individual-email"
                                formControlName="email"
                                matInput
                                placeholder="Email address"
                                />
                              @if (prescriberDetailsForm.get('email')?.invalid) {
                                <mat-error
                                  >
                                  Email address is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Alternative email address</mat-label>
                              <input
                                data-testid="individual-alt-email"
                                formControlName="emailAlt"
                                matInput
                                placeholder="Email address"
                                />
                              @if (prescriberDetailsForm.get('emailAlt')?.invalid) {
                                <mat-error
                                  >
                                  Alternative email address is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Number of dependants</mat-label>
                              <input
                                data-testid="individual-number-of-dependants"
                                type="number"
                                min="0"
                                formControlName="numberOfDependants"
                                matInput
                                placeholder="Number of Dependants"
                                />
                              @if (
                                prescriberDetailsForm.get('numberOfDependants')
                                ?.invalid
                                ) {
                                <mat-error
                                  >
                                  Number of dependants is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Individual type</mat-label>
                              <mat-select
                                required
                                data-testid="individual-type"
                                [disabled]="isLoading"
                                #chosenValue
                                formControlName="individualType"
                                >
                                @for (type of individualTypes; track type) {
                                  <mat-option
                                    [value]="type.id"
                                    >
                                    {{ type.type }}
                                  </mat-option>
                                }
                              </mat-select>
                              @if (
                                prescriberDetailsForm.get('individualType')?.invalid
                                ) {
                                <mat-error
                                  >
                                  Individual type is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field required appearance="outline">
                              <mat-label>Phone number</mat-label>
                              <input
                                required
                                data-testid="individual-phone-number"
                                formControlName="phoneNumber"
                                matInput
                                placeholder="Phone number"
                                />
                              @if (
                                prescriberDetailsForm.get('phoneNumber')?.invalid
                                ) {
                                <mat-error
                                  >
                                  Phone number is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                          <p>
                            <mat-form-field appearance="outline">
                              <mat-label>Alternative phone number</mat-label>
                              <input
                                data-testid="individual-alt-phone-number"
                                formControlName="phoneNumberAlt"
                                matInput
                                placeholder="Phone number"
                                />
                              @if (
                                prescriberDetailsForm.get('phoneNumberAlt')?.invalid
                                ) {
                                <mat-error
                                  >
                                  Alternative phone number is invalid
                                </mat-error>
                              }
                            </mat-form-field>
                          </p>
                        </div>
                      </div>
                    </form>
                  }
                </div>
              </mat-expansion-panel>
            }

            <mat-expansion-panel class="order-review-card__panel">
              <mat-expansion-panel-header>
                <mat-panel-title> Shipping details </mat-panel-title>
                <mat-panel-description> &nbsp; </mat-panel-description>
              </mat-expansion-panel-header>

              <!----------------------------------Display delivery address--------------------------------->

              <div class="details">
                <div class="my-2">
                  @if (labelLinks.length > 0) {
                    <div class="py-2">
                      <h3 class="my-2" mat-card-subtitle>Labels</h3>
                      @for (link of labelLinks; track link) {
                        <button
                          class="me-2"
                          mat-raised-button
                          color="primary"
                          (click)="viewLabel(link)"
                          >
                          <mat-icon>open_in_new</mat-icon> Download Label
                        </button>
                      }
                      @if (waybillNumber) {
                        <button
                          class="mx-1"
                          [disabled]="isLoading"
                          color="primary"
                          mat-raised-button
                          (click)="printDeliveryLabel()"
                          >
                          <mat-icon>print</mat-icon> Print Delivery Label
                        </button>
                      }
                    </div>
                  }

                  <div class="py-2">
                    <mat-divider></mat-divider>
                  </div>
                  <h3 class="my-2" mat-card-subtitle>Delivery address</h3>

                  @if (perm.canMutate()) {
                    <button
                      (click)="editingAddress = !editingAddress"
                      mat-flat-button
                      color="primary"
                      class="me-2"
                      >
                      {{ getEditAddressButtonText() }}
                    </button>
                  }

                  @if (!editingAddress && address) {
                    <table class="mt-4">
                      <tr>
                        <th>Delivery type</th>
                        <td>{{ getDeliveryType(order) }}</td>
                      </tr>
                      <tr>
                        <th>Address</th>
                        <td>{{ address.address_1 }}</td>
                      </tr>
                      <tr>
                        <th>Alternative address</th>
                        <td>{{ address.address_2 }}</td>
                      </tr>
                      <tr>
                        <th>Building number</th>
                        <td>{{ address.building_apt_number }}</td>
                      </tr>
                      <tr>
                        <th>Province</th>
                        <td>{{ address.province }}</td>
                      </tr>
                      <tr>
                        <th>City</th>
                        <td>{{ address.city }}</td>
                      </tr>
                      <tr>
                        <th>Postcode</th>
                        <td>{{ address.postcode }}</td>
                      </tr>
                      <tr>
                        <th>Country</th>
                        <td>{{ address.country }}</td>
                      </tr>
                    </table>
                  }
                </div>

                <!----------------------------------Edit prescriber details----------------------------------------->

                @if (editingAddress) {
                  <form
                    class="mt-4"
                    [formGroup]="addressDetailsForm"
                    >
                    <h3 class="my-2" mat-card-subtitle>Edit delivery address</h3>
                    <form [formGroup]="addressDetailsForm" class="row">
                      <p class="col-md-6">
                        <mat-form-field appearance="outline">
                          <mat-label>Search for a country</mat-label>
                          <input
                            required
                            [disabled]="isLoading"
                            type="text"
                            placeholder="Search for a country"
                            aria-label="Search for a country"
                            matInput
                            formControlName="country"
                            [matAutocomplete]="auto"
                            />
                          <mat-autocomplete
                            autoActiveFirstOption
                            #auto="matAutocomplete"
                            >
                            @for (country of filteredCountries | async; track country) {
                              <mat-option
                                [value]="country.name"
                                >
                                {{ country.name }}
                              </mat-option>
                            }
                          </mat-autocomplete>
                          <mat-icon matPrefix>search</mat-icon>
                          @if (addressDetailsForm.get('country')?.invalid) {
                            <mat-error
                              >
                              Country is invalid
                            </mat-error>
                          }
                        </mat-form-field>
                      </p>
                      <p class="col-md-6">
                        <mat-form-field appearance="outline">
                          <mat-label>Province</mat-label>
                          <mat-select
                            required
                            data-testid="individual-province"
                            formControlName="province"
                            >
                            @for (province of provinces; track province) {
                              <mat-option
                                value="{{ province.name }}"
                                >
                                {{ province.name }}
                              </mat-option>
                            }
                          </mat-select>
                          @if (addressDetailsForm.get('province')?.invalid) {
                            <mat-error
                              >
                              Province is invalid
                            </mat-error>
                          }
                        </mat-form-field>
                      </p>
                      <p class="col-md-6">
                        <mat-form-field appearance="outline">
                          <mat-label>City</mat-label>
                          <input type="text" formControlName="city" matInput />
                          @if (addressDetailsForm.get('city')?.invalid) {
                            <mat-error
                              >
                              City is invalid
                            </mat-error>
                          }
                        </mat-form-field>
                      </p>
                      <p class="col-md-6">
                        <mat-form-field appearance="outline">
                          <mat-label>Address line 1</mat-label>
                          <input
                            required
                            data-testid="individual-address-line-1"
                            formControlName="addressLine1"
                            matInput
                            placeholder="Address Line 1"
                            />
                          @if (addressDetailsForm.get('addressLine1')?.invalid) {
                            <mat-error
                              >
                              Address Line 1 is invalid
                            </mat-error>
                          }
                        </mat-form-field>
                      </p>
                      <p class="col-md-6">
                        <mat-form-field appearance="outline">
                          <mat-label>Suburb</mat-label>
                          <input
                            data-testid="individual-address-line-2"
                            formControlName="addressLine2"
                            matInput
                            placeholder="Suburb"
                            />
                          @if (addressDetailsForm.get('addressLine2')?.invalid) {
                            <mat-error
                              >
                              Suburb is invalid
                            </mat-error>
                          }
                        </mat-form-field>
                      </p>
                      <p class="col-md-6">
                        <mat-form-field appearance="outline">
                          <mat-label>Building Number</mat-label>
                          <input
                            required
                            data-testid="individual-building-apt-number"
                            formControlName="buildingAptNumber"
                            type="number"
                            min="1"
                            matInput
                            placeholder="Building Number"
                            />
                          @if (
                            addressDetailsForm.get('buildingAptNumber')?.invalid
                            ) {
                            <mat-error
                              >
                              Building Number is required
                            </mat-error>
                          }
                        </mat-form-field>
                      </p>
                      <p class="col-md-6">
                        <mat-form-field appearance="outline">
                          <mat-label>Postcode</mat-label>
                          <input
                            required
                            data-testid="individual-postcode"
                            formControlName="postcode"
                            matInput
                            placeholder="Postcode"
                            />
                          @if (addressDetailsForm.get('postcode')?.invalid) {
                            <mat-error
                              >
                              Postcode is invalid
                            </mat-error>
                          }
                        </mat-form-field>
                      </p>
                      <p class="col-md-6">
                        <mat-form-field appearance="outline">
                          <mat-label>Select method of delivery</mat-label>
                          <mat-select
                            required
                            data-testid="individual-delivery-type"
                            #chosenValue
                            formControlName="deliveryType"
                            >
                            @for (type of deliveryTypes; track type) {
                              <mat-option
                                [value]="type.id"
                                >
                                {{ type.type }}
                              </mat-option>
                            }
                          </mat-select>
                          @if (addressDetailsForm.get('deliveryType')?.invalid) {
                            <mat-error
                              >
                              Choose from provided options
                            </mat-error>
                          }
                        </mat-form-field>
                      </p>
                    </form>
                  </form>
                }
              </div>
            </mat-expansion-panel>
          </mat-accordion>
        </div>
        </mat-tab>
      </mat-tab-group>
  </div>
    @if (perm.canMutate()) {
      <div class="order-review-card__footer">
        <button
          class="order-review-card__footer-btn"
          [disabled]="isLoading"
          mat-stroked-button
          (click)="saveAndExit()"
        >
          <mat-icon>save</mat-icon> Save and exit
        </button>
        <button
          class="order-review-card__footer-btn--primary"
          [disabled]="isLoading"
          color="primary"
          mat-flat-button
          (click)="submitData()"
        >
          <mat-icon>save</mat-icon> Save
        </button>
      </div>
    }
  </mat-card>
</div>
